import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Fade,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";

// Utility functions
const usdFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const InquiryModal = ({ open, onClose, product, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    setIsSubmitting(false);
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const inquiryData = {
        ...formData,
        product: product.name,
        productBrand: product.brand,
        productPrice: product.priceUsd ? usdFmt.format(product.priceUsd) : 'Price on request'
      };
      
      onSubmit(inquiryData);
      handleClose();
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  }, [isSubmitting, resetForm, onClose]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ 
        timeout: { 
          enter: 400,
          exit: 300
        }
      }}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(3px)'
          }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 4 },
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: { xs: 1.5, sm: 2.5 },
        px: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            mb: 0.5,
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Product Inquiry
          </Typography>
          <Typography variant="caption" sx={{ 
            opacity: 0.9,
            fontSize: { xs: '0.7rem', sm: '0.75rem' }
          }}>
            Get in touch for more details
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          disabled={isSubmitting}
          size="small"
          sx={{ 
            color: 'white',
            position: 'relative',
            zIndex: 1,
            '&:hover': { 
              backgroundColor: 'rgba(255,255,255,0.15)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Product Info Section */}
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          bgcolor: 'grey.50', 
          borderBottom: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Box display="flex" gap={{ xs: 1.5, sm: 2 }} alignItems="center">
            {product.images?.[0] && (
              <Box
                component="img"
                src={product.images[0]}
                alt={product.name}
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  objectFit: 'cover',
                  borderRadius: { xs: 2, sm: 3 },
                  border: '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  flexShrink: 0
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="primary" sx={{ 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: { xs: '0.65rem', sm: '0.75rem' }
              }}>
                {product.brand}
              </Typography>
              <Typography variant="h6" sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem' }, 
                lineHeight: 1.3, 
                mb: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {product.name}
              </Typography>
              <Typography variant="h6" color="primary" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.2rem' }
              }}>
                {product.priceUsd ? usdFmt.format(product.priceUsd) : 'Price on request'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Form Section */}
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="subtitle1" gutterBottom sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}>
            Your Contact Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
            <TextField
              autoFocus
              label="Full Name"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isSubmitting}
              InputProps={{
                sx: { 
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
            
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              size="small"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
              InputProps={{
                sx: { 
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
            
            <TextField
              label="Phone Number (Optional)"
              type="tel"
              fullWidth
              variant="outlined"
              size="small"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              disabled={isSubmitting}
              InputProps={{
                sx: { 
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />

            <TextField
              label="Additional Message (Optional)"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              size="small"
              value={formData.message}
              onChange={handleInputChange('message')}
              placeholder="Any specific questions or requirements..."
              disabled={isSubmitting}
              InputProps={{
                sx: { 
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: { xs: 2, sm: 4 }, 
        pb: { xs: 2, sm: 4 }, 
        pt: 0,
        gap: { xs: 1, sm: 2 },
        bgcolor: 'grey.50',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          variant="outlined"
          disabled={isSubmitting}
          fullWidth={{ xs: true, sm: false }}
          sx={{ 
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontWeight: 600,
            minWidth: { xs: 'auto', sm: 100 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
          fullWidth={{ xs: true, sm: false }}
          sx={{ 
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontWeight: 600,
            minWidth: { xs: 'auto', sm: 140 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            }
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Inquiry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InquiryModal;